import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ArrowLeftRight } from "lucide-react";
import { db } from "../../../firebase";

// 曲情報1セット（曲名・作詞者・作曲者・編曲者）分のstateとsetterをまとめて扱うための型。
// 曲順の入れ替えボタンで使用する。
interface SongInfoFields {
  name: string;
  setName: (value: string) => void;
  lyrics: string;
  setLyrics: (value: string) => void;
  composed: string;
  setComposed: (value: string) => void;
  arranged: string;
  setArranged: (value: string) => void;
}

// 曲名・作詞者・作曲者・編曲者の4項目を、1個ずつコピーせずに
// まとめて入れ替えるためのヘルパー。
const swapSongInfo = (songA: SongInfoFields, songB: SongInfoFields) => {
  const temp = {
    name: songA.name,
    lyrics: songA.lyrics,
    composed: songA.composed,
    arranged: songA.arranged,
  };
  songA.setName(songB.name);
  songA.setLyrics(songB.lyrics);
  songA.setComposed(songB.composed);
  songA.setArranged(songB.arranged);
  songB.setName(temp.name);
  songB.setLyrics(temp.lyrics);
  songB.setComposed(temp.composed);
  songB.setArranged(temp.arranged);
};

interface GroupEditProps {
  userId?: string;
  onBack: () => void;
}

const GroupEdit: React.FC<GroupEditProps> = ({ userId, onBack }) => {
  const [groupName, setGroupName] = useState("");
  const [groupNameRuby, setGroupNameRuby] = useState("");
  const [entryClass, setEntryClass] = useState("QuartetContest");
  const [picName, setPicName] = useState("");
  const [picMail, setPicMail] = useState("");
  const [entryNumber, setEntryNumber] = useState("");
  const [quartetContestMemberTenor, setQuartetContestMemberTenor] =
    useState("");
  const [quartetContestMemberLead, setQuartetContestMemberLead] = useState("");
  const [quartetContestMemberBaritone, setQuartetContestMemberBaritone] =
    useState("");
  const [quartetContestMemberBass, setQuartetContestMemberBass] = useState("");
  const [chorusContestAttendance, setChorusContestAttendance] =
    useState<string>("");
  const [chorusDirector, setChorusDirector] = useState("");
  const [information, setInformation] = useState("");
  const [songName1, setSongName1] = useState("");
  const [songLyrics1, setSongLyrics1] = useState("");
  const [songComposed1, setSongComposed1] = useState("");
  const [songArranged1, setSongArranged1] = useState("");
  const [songName2, setSongName2] = useState("");
  const [songLyrics2, setSongLyrics2] = useState("");
  const [songComposed2, setSongComposed2] = useState("");
  const [songArranged2, setSongArranged2] = useState("");
  const [songName3, setSongName3] = useState("");
  const [songLyrics3, setSongLyrics3] = useState("");
  const [songComposed3, setSongComposed3] = useState("");
  const [songArranged3, setSongArranged3] = useState("");
  const [songName4, setSongName4] = useState("");
  const [songLyrics4, setSongLyrics4] = useState("");
  const [songComposed4, setSongComposed4] = useState("");
  const [songArranged4, setSongArranged4] = useState("");
  const [scoreFile, setScoreFile] = useState<boolean>(false);
  const [performLicense, setPerformLicense] = useState<boolean>(false);
  const [inviteChat, setInviteChat] = useState<boolean>(false);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const [profileFormationEpisode, setProfileFormationEpisode] = useState("");
  const [profileMemberRelationship, setProfileMemberRelationship] =
    useState("");
  const [profileMcMention, setProfileMcMention] = useState("");
  const [profileLocalPride, setProfileLocalPride] = useState("");
  const [profileActivityThisYear, setProfileActivityThisYear] = useState("");
  const [profileMotivation, setProfileMotivation] = useState("");
  const [profileMessageToAudience, setProfileMessageToAudience] = useState("");
  const [joinChampionship, setJoinChampionship] = useState<boolean>(false);
  const [joinPreliminary, setJoinPreliminary] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);

  // 1曲目・2曲目（SemiFinal）の曲情報4項目をまとめて入れ替える
  const handleSwapSong1And2 = () =>
    swapSongInfo(
      {
        name: songName1,
        setName: setSongName1,
        lyrics: songLyrics1,
        setLyrics: setSongLyrics1,
        composed: songComposed1,
        setComposed: setSongComposed1,
        arranged: songArranged1,
        setArranged: setSongArranged1,
      },
      {
        name: songName2,
        setName: setSongName2,
        lyrics: songLyrics2,
        setLyrics: setSongLyrics2,
        composed: songComposed2,
        setComposed: setSongComposed2,
        arranged: songArranged2,
        setArranged: setSongArranged2,
      },
    );

  // 1曲目・2曲目（Final）の曲情報4項目をまとめて入れ替える
  const handleSwapSong3And4 = () =>
    swapSongInfo(
      {
        name: songName3,
        setName: setSongName3,
        lyrics: songLyrics3,
        setLyrics: setSongLyrics3,
        composed: songComposed3,
        setComposed: setSongComposed3,
        arranged: songArranged3,
        setArranged: setSongArranged3,
      },
      {
        name: songName4,
        setName: setSongName4,
        lyrics: songLyrics4,
        setLyrics: setSongLyrics4,
        composed: songComposed4,
        setComposed: setSongComposed4,
        arranged: songArranged4,
        setArranged: setSongArranged4,
      },
    );

  useEffect(() => {
    if (!userId) return; // 新規作成時はfetchしない
    const fetchGroup = async () => {
      const docRef = doc(db, "groups", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGroupName(data.groupName || "");
        setGroupNameRuby(data.groupNameRuby || "");
        setEntryClass(data.entryClass || "QuartetContest");
        setPicName(data.picName || "");
        setPicMail(data.picMail || "");
        // entryNumberはFirestore上では数値で保存されていることがあるため、
        // 必ず文字列に変換してstateへ入れる。数値のまま入れてしまうと、
        // 保存時に resolvedEntryNumber.trim() が「数値にtrimは無い」で
        // 例外を投げ、保存ボタンを押しても何も起きない不具合になっていた。
        setEntryNumber(
          data.entryNumber !== undefined && data.entryNumber !== null
            ? String(data.entryNumber)
            : "",
        );
        setQuartetContestMemberTenor(data.quartetContestMemberTenor || "");
        setQuartetContestMemberLead(data.quartetContestMemberLead || "");
        setQuartetContestMemberBaritone(
          data.quartetContestMemberBaritone || "",
        );
        setQuartetContestMemberBass(data.quartetContestMemberBass || "");
        setChorusContestAttendance(
          data.chorusContestAttendance !== undefined &&
            data.chorusContestAttendance !== null
            ? String(data.chorusContestAttendance)
            : "",
        );
        setChorusDirector(data.chorusDirector || "");
        setInformation(data.information || "");
        setScoreFile(!!data.scoreFile);
        setPerformLicense(!!data.performLicense);
        setInviteChat(!!data.inviteChat);
        setIsDeleted(!!data.isDeleted);
        setSongName1(data.songName1 || "");
        setSongLyrics1(data.songLyrics1 || "");
        setSongComposed1(data.songComposed1 || "");
        setSongArranged1(data.songArranged1 || "");
        setSongName2(data.songName2 || "");
        setSongLyrics2(data.songLyrics2 || "");
        setSongComposed2(data.songComposed2 || "");
        setSongArranged2(data.songArranged2 || "");
        setSongName3(data.songName3 || "");
        setSongLyrics3(data.songLyrics3 || "");
        setSongComposed3(data.songComposed3 || "");
        setSongArranged3(data.songArranged3 || "");
        setSongName4(data.songName4 || "");
        setSongLyrics4(data.songLyrics4 || "");
        setSongComposed4(data.songComposed4 || "");
        setSongArranged4(data.songArranged4 || "");
        setProfileFormationEpisode(data.profileFormationEpisode || "");
        setProfileMemberRelationship(data.profileMemberRelationship || "");
        setProfileMcMention(data.profileMcMention || "");
        setProfileLocalPride(data.profileLocalPride || "");
        setProfileActivityThisYear(data.profileActivityThisYear || "");
        setProfileMotivation(data.profileMotivation || "");
        setProfileMessageToAudience(data.profileMessageToAudience || "");
        setJoinChampionship(!!data.joinChampionship);
        setJoinPreliminary(!!data.joinPreliminary);
      }
    };
    fetchGroup();
  }, [userId]);

  const getEntryCountForYearAndClass = async (
    year: number,
    entryClass: string,
  ): Promise<number> => {
    const start = new Date(`${year}-01-01T00:00:00`);
    const end = new Date(`${year + 1}-01-01T00:00:00`);

    const groupRef = collection(db, "groups");
    const classAndYearQuery = query(
      groupRef,
      where("entryClass", "==", entryClass),
      where("createdAt", ">=", start),
      where("createdAt", "<", end),
    );
    const snapshot = await getDocs(classAndYearQuery);
    return snapshot.size;
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!groupName.trim()) {
      alert("グループ名は必須です。");
      return;
    }

    setIsSaving(true);

    try {
      // entryNumberが数値のまま渡ってくるケースがあっても落ちないよう、
      // trimする前に必ず文字列化しておく。
      let resolvedEntryNumber = String(entryNumber ?? "");

      if (!resolvedEntryNumber.trim()) {
        const thisYear = new Date().getFullYear();
        const count = await getEntryCountForYearAndClass(
          thisYear,
          entryClass,
        );
        resolvedEntryNumber = String(count + 1);
      }

      const groupData = {
        groupName,
        groupNameRuby,
        entryClass,
        picName,
        picMail,
        entryNumber: resolvedEntryNumber,
        quartetContestMemberTenor,
        quartetContestMemberLead,
        quartetContestMemberBaritone,
        quartetContestMemberBass,
        chorusContestAttendance: Number(chorusContestAttendance) || 0,
        chorusDirector,
        information,
        songName1,
        songLyrics1,
        songComposed1,
        songArranged1,
        songName2,
        songLyrics2,
        songComposed2,
        songArranged2,
        songName3,
        songLyrics3,
        songComposed3,
        songArranged3,
        songName4,
        songLyrics4,
        songComposed4,
        songArranged4,
        scoreFile,
        performLicense,
        inviteChat,
        isDeleted: userId ? isDeleted : false,
        profileFormationEpisode,
        profileMemberRelationship,
        profileMcMention,
        profileLocalPride,
        profileActivityThisYear,
        profileMotivation,
        profileMessageToAudience,
        joinChampionship,
        joinPreliminary,
        ...(userId ? {} : { createdAt: serverTimestamp() }),
      };

      if (userId) {
        // 編集（update）
        await updateDoc(doc(db, "groups", userId), {
          ...groupData,
          updatedAt: serverTimestamp(),
        });
      } else {
        // 新規登録（add）
        await addDoc(collection(db, "groups"), {
          ...groupData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      onBack();
    } catch (error) {
      // 以前はここでエラーを捕捉していなかったため、保存に失敗しても
      // ボタンを押した後は何も起きないように見えていた。
      console.error("グループ情報の保存エラー:", error);
      alert(
        "保存に失敗しました。時間をおいて再度お試しください。\n（詳細はブラウザのコンソールをご確認ください）",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="table-contents">
      <p className="back" onClick={onBack}>
        ＜ 一覧に戻る
      </p>
      <div className="table-main">
        <div className="admin-edit entry-form">
          <div className="form-contents short-size">
            <label className="form-label required">No.{entryNumber}</label>
          </div>
          <div className="form-contents middle-size">
            <label className="form-label required">グループ名</label>
            <input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size">
            <label className="form-label required">
              グループ名（ふりがな）
            </label>
            <input
              value={groupNameRuby}
              onChange={(e) => setGroupNameRuby(e.target.value)}
            />
          </div>
          <div className="form-contents section-start">
            <label className="form-label required">エントリークラス</label>
            <div className="entry-class-options">
              <label
                className={`entry-class-option quartet ${
                  entryClass === "QuartetContest" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  value="QuartetContest"
                  checked={entryClass === "QuartetContest"}
                  onChange={() => setEntryClass("QuartetContest")}
                />
                Quartet Contest
              </label>
              <label
                className={`entry-class-option chorus ${
                  entryClass === "ChorusContest" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  value="ChorusContest"
                  checked={entryClass === "ChorusContest"}
                  onChange={() => setEntryClass("ChorusContest")}
                />
                Chorus Contest
              </label>
              <label
                className={`entry-class-option evaluation ${
                  entryClass === "EvaluationOnly" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  value="EvaluationOnly"
                  checked={entryClass === "EvaluationOnly"}
                  onChange={() => setEntryClass("EvaluationOnly")}
                />
                Evaluation Only
              </label>
            </div>
          </div>
          <div className="form-contents section-start">
            <label className="form-label">Championship</label>
            <button
              type="button"
              className={`confirmation-button ${
                joinChampionship ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setJoinChampionship(!joinChampionship)}
            >
              {joinChampionship ? "参加" : "不参加"}
            </button>
          </div>
          <div className="form-contents">
            <label className="form-label">Preliminary</label>
            <button
              type="button"
              className={`confirmation-button ${
                joinPreliminary ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setJoinPreliminary(!joinPreliminary)}
            >
              {joinPreliminary ? "参加" : "不参加"}
            </button>
          </div>
          <div className="form-contents middle-size  section-start">
            <label className="form-label required">連絡担当者名</label>
            <input
              value={picName}
              onChange={(e) => setPicName(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size">
            <label className="form-label required">連絡担当者メール</label>
            <input
              value={picMail}
              onChange={(e) => setPicMail(e.target.value)}
            />
          </div>
          {entryClass === "QuartetContest" && (
            <>
              <div className="form-contents middle-size section-start">
                <label className="form-label required">
                  カルテットメンバー
                </label>
              </div>
              <div className="form-contents middle-size second">
                <label className="form-label">メンバー1</label>
                <input
                  value={quartetContestMemberTenor}
                  onChange={(e) => setQuartetContestMemberTenor(e.target.value)}
                />
              </div>

              <div className="form-contents middle-size second">
                <label className="form-label">メンバー2</label>
                <input
                  value={quartetContestMemberLead}
                  onChange={(e) => setQuartetContestMemberLead(e.target.value)}
                />
              </div>

              <div className="form-contents middle-size second">
                <label className="form-label">メンバー3</label>
                <input
                  value={quartetContestMemberBaritone}
                  onChange={(e) =>
                    setQuartetContestMemberBaritone(e.target.value)
                  }
                />
              </div>

              <div className="form-contents middle-size second">
                <label className="form-label">メンバー4</label>
                <input
                  value={quartetContestMemberBass}
                  onChange={(e) => setQuartetContestMemberBass(e.target.value)}
                />
              </div>
            </>
          )}
          {entryClass !== "QuartetContest" && (
            <div className="form-contents short-size section-start">
              <label className="form-label">出演者人数</label>
              <input
                value={chorusContestAttendance}
                onChange={(e) => setChorusContestAttendance(e.target.value)}
              />
              <span>人</span>
            </div>
          )}
          {entryClass === "ChorusContest" && (
            <div className="form-contents middle-size">
              <label className="form-label">Director氏名</label>
              <input
                value={chorusDirector}
                onChange={(e) => setChorusDirector(e.target.value)}
              />
            </div>
          )}
          <div className="form-contents section-start">
            <label className="form-label">
              {entryClass === "QuartetContest" && "SemiFinal "}曲情報
            </label>
          </div>
          <div className="form-contents second">
            <label className="form-label">1曲目</label>
            <input
              value={songName1}
              onChange={(e) => setSongName1(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">作詞者</label>
            <input
              value={songLyrics1}
              onChange={(e) => setSongLyrics1(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">作曲者</label>
            <input
              value={songComposed1}
              onChange={(e) => setSongComposed1(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">編曲者</label>
            <input
              value={songArranged1}
              onChange={(e) => setSongArranged1(e.target.value)}
            />
          </div>
          <div
            className="form-contents section-start"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              type="button"
              onClick={handleSwapSong1And2}
              title="1曲目と2曲目の内容を入れ替え"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px 3px",
                fontSize: "12px",
                border: "1px solid #999999",
                borderRadius: "20px",
                background: "#fff",
                color: "#999999",
                cursor: "pointer",
              }}
            >
              <ArrowLeftRight size={14} />
              1曲目と2曲目を入れ替え
            </button>
          </div>
          <div className="form-contents second section-start">
            <label className="form-label">2曲目</label>
            <input
              value={songName2}
              onChange={(e) => setSongName2(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">作詞者</label>
            <input
              value={songLyrics2}
              onChange={(e) => setSongLyrics2(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">作曲者</label>
            <input
              value={songComposed2}
              onChange={(e) => setSongComposed2(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size third">
            <label className="form-label">編曲者</label>
            <input
              value={songArranged2}
              onChange={(e) => setSongArranged2(e.target.value)}
            />
          </div>
          {entryClass === "QuartetContest" && (
            <>
              <div className="form-contents section-start">
                <label className="form-label">Final 曲情報</label>
              </div>
              <div className="form-contents second">
                <label className="form-label">1曲目</label>
                <input
                  value={songName3}
                  onChange={(e) => setSongName3(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">作詞者</label>
                <input
                  value={songLyrics3}
                  onChange={(e) => setSongLyrics3(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">作曲者</label>
                <input
                  value={songComposed3}
                  onChange={(e) => setSongComposed3(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">編曲者</label>
                <input
                  value={songArranged3}
                  onChange={(e) => setSongArranged3(e.target.value)}
                />
              </div>
              <div
                className="form-contents section-start"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  onClick={handleSwapSong3And4}
                  title="1曲目と2曲目の内容を入れ替え"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px 3px",
                    fontSize: "12px",
                    border: "1px solid #999999",
                    borderRadius: "20px",
                    background: "#fff",
                    color: "#999999",
                    cursor: "pointer",
                  }}
                >
                  <ArrowLeftRight size={14} />
                  1曲目と2曲目を入れ替え
                </button>
              </div>
              <div className="form-contents second section-start">
                <label className="form-label">2曲目</label>
                <input
                  value={songName4}
                  onChange={(e) => setSongName4(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">作詞者</label>
                <input
                  value={songLyrics4}
                  onChange={(e) => setSongLyrics4(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">作曲者</label>
                <input
                  value={songComposed4}
                  onChange={(e) => setSongComposed4(e.target.value)}
                />
              </div>
              <div className="form-contents middle-size third">
                <label className="form-label">編曲者</label>
                <input
                  value={songArranged4}
                  onChange={(e) => setSongArranged4(e.target.value)}
                />
              </div>
            </>
          )}
          <div className="form-contents section-start">
            <label className="form-label">楽譜提出</label>
            <button
              type="button"
              className={`confirmation-button ${
                scoreFile ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setScoreFile(!scoreFile)}
            >
              {scoreFile ? "確認済" : "未確認"}
            </button>
          </div>
          <div className="form-contents">
            <label className="form-label">ライセンス提出</label>
            <button
              type="button"
              className={`confirmation-button ${
                performLicense ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setPerformLicense(!performLicense)}
            >
              {performLicense ? "確認済" : "未確認"}
            </button>
          </div>
          <div className="form-contents">
            <label className="form-label">チャット招待</label>
            <button
              type="button"
              className={`confirmation-button ${
                inviteChat ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setInviteChat(!inviteChat)}
            >
              {inviteChat ? "確認済" : "未確認"}
            </button>
          </div>
          {/* <div className="form-contents section-start">
            <label className="form-label">プロフィール</label>
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">結成エピソード</label>
            <textarea
              value={profileFormationEpisode}
              onChange={(e) => setProfileFormationEpisode(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">メンバーの関係性</label>
            <textarea
              value={profileMemberRelationship}
              onChange={(e) => setProfileMemberRelationship(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">触れて欲しいこと</label>
            <textarea
              value={profileMcMention}
              onChange={(e) => setProfileMcMention(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">地元自慢</label>
            <textarea
              value={profileLocalPride}
              onChange={(e) => setProfileLocalPride(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">この1年の活動</label>
            <textarea
              value={profileActivityThisYear}
              onChange={(e) => setProfileActivityThisYear(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">今回の意気込み</label>
            <textarea
              value={profileMotivation}
              onChange={(e) => setProfileMotivation(e.target.value)}
            />
          </div>
          <div className="form-contents middle-size second">
            <label className="form-label">お客様へ一言</label>
            <textarea
              value={profileMessageToAudience}
              onChange={(e) => setProfileMessageToAudience(e.target.value)}
            />
          </div> */}
          <div className="form-contents section-start">
            <label className="form-label">連絡事項</label>
            <input
              value={information}
              onChange={(e) => setInformation(e.target.value)}
            />
          </div>
          <div
            className="form-contents section-start"
            style={{ justifyContent: "end", textAlign: "right" }}
          >
            <label className="form-label" style={{ marginRight: "10px" }}>
              グループ削除
            </label>
            <button
              type="button"
              className={`delete-button ${
                isDeleted ? "confirmed" : "unconfirmed"
              }`}
              onClick={() => setIsDeleted(!isDeleted)}
            >
              削除する
            </button>
          </div>
          <div className="edit-footer">
            <button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving
                ? "保存中…"
                : userId
                  ? "編集を保存する"
                  : "新規登録する"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupEdit;
